import { Box } from '@coolblue-development/becky';
import ReplyButton from './ReplyButton';
import { useEmailContext } from '../../../../../contexts/EmailContext';
import { EmailContactData } from '../../../../../models/EmailContact';
import ReplyHeader from './ReplyHeader';
import ReplyInput from './ReplyInput/ReplyInput';
import { Snackbar as FreddySnackbar } from '@coolblue-development/freddy';
import { useSnackbar } from '../../../../../utils/hooks/useSnackbar';
import { datadogLogs } from '@datadog/browser-logs';
import { useEffect, useRef, useState } from 'react';
import { EmailClient } from '@amazon-connect/email';
import { EMAIL_INITIATION_METHODS } from '../../../../../constants/email';
import { fetchContact } from '../../../../../utils/fetchContact';
import { endConversation } from '../../../../../utils/endConversation';
import TransferButton from '../Transfer/TransferButton';
import TransferEmailModal from '../Transfer/TransferEmailModal';
import { transferEmail } from '../../../../../services/transferEmail';
import type { Queue } from '../../../../../models/Queue';
import { useAppContext } from '../../../../../AppContext';

const ReplyContainer = (): JSX.Element | null => {
  const { emailData, selectedEmail, isReplyOpen, setIsReplyOpen, updateEmailData, setThreadTransferDetails } =
    useEmailContext();
  const { agent } = useAppContext();
  const data: EmailContactData | undefined = selectedEmail ? emailData[selectedEmail] : undefined;
  const { snackbar, show, hideSnackbar } = useSnackbar();
  const emailClientRef = useRef<EmailClient | null>(null);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  useEffect(() => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
      emailClientRef.current = new EmailClient(connect.core.getSDKClientConfig() as any);
    } catch (error) {
      datadogLogs.logger.error('Failed to initialize EmailClient', { channel: 'email' }, error as Error);
    }
  }, []);

  if (!selectedEmail) {
    return null;
  }

  const contact = fetchContact(selectedEmail);
  const isInAfterContactWork = contact?.getStatus().type === connect.ContactStateType.ENDED;

  const handleDiscard = (): void => {
    setIsReplyOpen(false);

    const draftContactId = data?.replyDraftContactId;
    if (!draftContactId) return;

    const draftContact = fetchContact(draftContactId);
    if (!draftContact) return;

    endConversation(
      draftContact,
      () => updateEmailData(selectedEmail, { ...data, replyDraftContactId: undefined }),
      () => {
        show('Failed to discard reply. Please try again.', 'error');
        setIsReplyOpen(true);
      }
    );
  };

  const handleTransfer = async (queue: Queue): Promise<void> => {
    if (!selectedEmail || !queue) return;

    try {
      await transferEmail(selectedEmail, queue.id);

      datadogLogs.logger.info('Email transfer succeeded', {
        destinationQueueName: queue.name,
        contactId: selectedEmail
      });

      setThreadTransferDetails(selectedEmail, { toQueue: queue.name, byAgent: agent?.getName() });
      show('Email transferred successfully!', 'success');
    } catch (error) {
      datadogLogs.logger.error('Failed to transfer email', { channel: 'email' }, error as Error);
      show('Failed to transfer the email. Please try again.', 'error');
    }
  };

  const handleReplyButtonClick = async (): Promise<void> => {
    setIsReplyOpen(true);

    if (!emailClientRef.current) return;

    if (data?.replyDraftContactId) {
      return;
    }

    try {
      const { contactId } = await emailClientRef.current.createDraftEmail({
        initiationMethod: EMAIL_INITIATION_METHODS.AGENT_REPLY,
        relatedContactId: selectedEmail
      });

      if (!contactId) {
        throw new Error('createDraftEmail returned no contactId');
      }

      updateEmailData(selectedEmail, { ...data, contactId: selectedEmail, replyDraftContactId: contactId });
    } catch (error) {
      datadogLogs.logger.error('Failed to create reply draft', { channel: 'email' }, error as Error);
      show('Failed to start reply. Please try again.', 'error');
      setIsReplyOpen(false);
    }
  };

  return (
    <Box sx={{ margin: '16px' }}>
      {isReplyOpen ? (
        <>
          {data && <ReplyHeader data={data} />}

          <ReplyInput
            onDiscard={handleDiscard}
            onSuccess={() => {
              show('Email sent successfully!', 'success');
              updateEmailData(selectedEmail, { ...data, contactId: selectedEmail, replyDraftContactId: undefined });
            }}
            onError={message => show(message, 'error')}
            onAttachmentUploadError={message => show(message, 'error')}
          />
        </>
      ) : (
        data && (
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <ReplyButton onClick={handleReplyButtonClick} />
            <TransferButton
              disabled={isInAfterContactWork}
              onClick={() => {
                datadogLogs.logger.info('Email transfer modal opened');
                setIsTransferModalOpen(true);
              }}
            />
          </Box>
        )
      )}

      <TransferEmailModal
        open={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        onTransfer={queue => {
          void handleTransfer(queue);
        }}
      />

      <FreddySnackbar
        open={snackbar.open}
        title={snackbar.title}
        intent={snackbar.intent}
        duration={snackbar.duration}
        onClose={hideSnackbar}
        placement="bottom"
      />
    </Box>
  );
};

export default ReplyContainer;
